import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useUserTier } from '../hooks/useUserTier';
import { supabase } from '../lib/supabase';
import { trackFunnelEvent } from '../lib/funnel-tracking';
import { canAccessPlanet, getLockedPlanets } from '../lib/paywall-rules';
import { BirthChartVisual } from '../components/natal-chart/BirthChartVisual';
import { SoftPaywallBanner } from '../components/paywall/SoftPaywallBanner';
import { UpsellModal } from '../components/paywall/UpsellModal';

// ─── Types ───────────────────────────────────────────────────────────────────

interface Planet { sign: string; degree: number }

interface Interpretation {
  text:    string;
  preview: string;
  isFull:  boolean;
}

interface NatalChart {
  id:             string;
  planets:        Record<string, Planet>;
  ascendant_sign: string | null;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PT_NAME: Record<string, string> = {
  sun:'Sol', moon:'Lua', ascendant:'Ascendente',
  mercury:'Mercúrio', venus:'Vênus', mars:'Marte',
  jupiter:'Júpiter', saturn:'Saturno', uranus:'Urano', neptune:'Netuno', pluto:'Plutão',
};
const GLYPH: Record<string, string> = {
  sun:'☉', moon:'☽', mercury:'☿', venus:'♀', mars:'♂',
  jupiter:'♃', saturn:'♄', uranus:'♅', neptune:'♆', pluto:'♇', ascendant:'AC',
};
const FREE_ORDER  = ['sun', 'moon', 'ascendant'] as const;
const LOCKED_ORDER = ['mercury', 'venus', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'] as const;

// ─── Sub-components ──────────────────────────────────────────────────────────

function UnlockedCard({
  planetKey,
  planet,
  text,
}: {
  planetKey: string;
  planet:    Planet;
  text:      string;
}) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (expanded) trackFunnelEvent('read_free_interpretation', { planet: planetKey });
  }, [expanded, planetKey]);

  return (
    <div style={{
      background:   'var(--bg-elevated)',
      border:       '1px solid var(--line-soft)',
      borderRadius: 'var(--radius-md)',
      padding:      '1.25rem 1.5rem',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '1.1rem', color: 'var(--petrol)' }}>{GLYPH[planetKey]}</span>
        <h3 style={{ margin: 0, fontSize: 'var(--text-15)', fontWeight: 600, color: 'var(--bg)' }}>
          {PT_NAME[planetKey]} em {planet.sign}
        </h3>
        <span style={{ marginLeft: 'auto', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
          {Math.round(planet.degree)}°
        </span>
      </div>

      {text ? (
        <>
          <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-secondary)', lineHeight: 1.65 }}>
            {expanded ? text : text.slice(0, 180) + (text.length > 180 ? '…' : '')}
          </p>
          {text.length > 180 && (
            <button
              onClick={() => setExpanded(e => !e)}
              style={{ marginTop: '0.5rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-caption)', color: 'var(--petrol)', padding: 0, fontWeight: 600 }}
            >
              {expanded ? 'Mostrar menos ▲' : 'Ler interpretação completa ▼'}
            </button>
          )}
        </>
      ) : (
        <div style={{ height: '3rem', background: 'var(--bg-base)', borderRadius: 'var(--radius-sm)', animation: 'pulse 1.5s ease-in-out infinite' }} />
      )}
    </div>
  );
}

function LockedCard({
  planetKey,
  planet,
  preview,
  onUnlock,
}: {
  planetKey: string;
  planet:    Planet;
  preview:   string;
  onUnlock:  () => void;
}) {
  function handleClick() {
    trackFunnelEvent('hit_paywall', { planet: planetKey });
    onUnlock();
  }

  return (
    <div
      style={{
        background:   'var(--bg-elevated)',
        border:       '1px solid var(--line-soft)',
        borderRadius: 'var(--radius-md)',
        padding:      '1.25rem 1.5rem',
        position:     'relative',
        overflow:     'hidden',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
        <span style={{ fontSize: '1.1rem', color: 'var(--petrol)' }}>{GLYPH[planetKey]}</span>
        <h3 style={{ margin: 0, fontSize: 'var(--text-15)', fontWeight: 600, color: 'var(--bg)' }}>
          {PT_NAME[planetKey]} em {planet.sign}
        </h3>
        <span style={{ marginLeft: 'auto', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
          {Math.round(planet.degree)}°
        </span>
      </div>

      {/* Blurred teaser */}
      <p style={{ margin: '0 0 0.75rem', fontSize: 'var(--text-label)', color: 'var(--text-secondary)', lineHeight: 1.65, filter: 'blur(4px)', userSelect: 'none' }}>
        {preview || 'Sua interpretação personalizada está pronta e aguardando...'}
      </p>

      {/* Overlay */}
      <div style={{
        position:   'absolute',
        inset:      0,
        background: 'linear-gradient(to bottom, transparent 40%, var(--bg-elevated) 70%)',
        display:    'flex',
        alignItems: 'flex-end',
        justifyContent: 'center',
        padding:    '1rem',
      }}>
        <button
          onClick={handleClick}
          style={{
            background:   'var(--petrol)',
            color:        '#fff',
            border:       'none',
            borderRadius: 'var(--radius-sm)',
            padding:      '0.5rem 1.25rem',
            fontSize:     'var(--text-label)',
            fontWeight:   600,
            cursor:       'pointer',
            display:      'flex',
            alignItems:   'center',
            gap:          '0.4rem',
          }}
        >
          🔒 Desbloquear
        </button>
      </div>
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function MapaPage() {
  const { user, loading: authLoading } = useAuth();
  const { tier, isPaid }               = useUserTier();
  const navigate                       = useNavigate();

  const [chart, setChart]                     = useState<NatalChart | null>(null);
  const [interpretations, setInterpretations] = useState<Record<string, Interpretation>>({});
  const [loadingChart, setLoadingChart]       = useState(true);
  const [loadingInterp, setLoadingInterp]     = useState(false);
  const [interpError, setInterpError]         = useState<string | null>(null);
  const [modalOpen, setModalOpen]             = useState(false);

  // Redirect if not logged in
  useEffect(() => {
    if (!authLoading && !user) navigate('/register', { replace: true });
  }, [authLoading, user, navigate]);

  // Fetch natal chart
  useEffect(() => {
    if (!user) return;
    supabase
      .from('natal_charts')
      .select('id, planets, ascendant_sign')
      .eq('user_id', user.id)
      .order('calculated_at', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        setChart(data as NatalChart | null);
        setLoadingChart(false);
      });
  }, [user]);

  // Generate / load interpretations once chart is ready
  useEffect(() => {
    if (!chart) return;
    setLoadingInterp(true);
    setInterpError(null);

    supabase.functions
      .invoke('generate-free-interpretations', { body: { chart_id: chart.id } })
      .then(({ data, error }) => {
        if (error || !data?.interpretations) {
          setInterpError('Não foi possível gerar as interpretações agora. Tente novamente.');
          return;
        }
        setInterpretations(data.interpretations as Record<string, Interpretation>);
      })
      .finally(() => setLoadingInterp(false));
  }, [chart]);

  function openModal() {
    trackFunnelEvent('clicked_upgrade_cta', { location: 'mapa' });
    setModalOpen(true);
  }

  if (authLoading || loadingChart) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', color: 'var(--text-muted)' }}>
        Carregando mapa natal…
      </div>
    );
  }

  if (!chart) {
    return (
      <div style={{ maxWidth: '560px', margin: '4rem auto', padding: '0 1rem', textAlign: 'center' }}>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '1rem' }}>Você ainda não tem um mapa natal gerado.</p>
        <button onClick={() => navigate('/onboarding')} className="btn btn-primary">
          Gerar meu mapa natal
        </button>
      </div>
    );
  }

  const allPlanets = { ...chart.planets };
  if (chart.ascendant_sign && !allPlanets['ascendant']) {
    allPlanets['ascendant'] = { sign: chart.ascendant_sign, degree: 0 };
  }

  return (
    <>
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '1.5rem 1rem 4rem' }}>

        {/* Page header */}
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'var(--text-28)', color: 'var(--bg)', margin: '0 0 0.25rem' }}>
            Seu Mapa Natal ✦
          </h1>
          <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-muted)' }}>
            {isPaid ? 'Acesso completo · todos os planetas desbloqueados' : '3 planetas grátis · desbloqueie os demais com Premium'}
          </p>
        </div>

        {/* Chart wheel */}
        <div style={{ marginBottom: '2rem', background: 'var(--bg-elevated)', border: '1px solid var(--line-soft)', borderRadius: 'var(--radius-md)', padding: '1.5rem' }}>
          <BirthChartVisual
            planets={chart.planets}
            ascendantSign={chart.ascendant_sign}
          />
        </div>

        {/* Interpretation error */}
        {interpError && (
          <div style={{ background: '#fff3cd', border: '1px solid #ffe08a', borderRadius: 'var(--radius-sm)', padding: '0.75rem 1rem', marginBottom: '1.5rem', color: '#856404', fontSize: 'var(--text-label)' }}>
            {interpError}
          </div>
        )}

        {/* Free interpretations */}
        <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'var(--text-22)', color: 'var(--bg)', margin: '0 0 1rem' }}>
          Interpretações Gratuitas
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginBottom: '2rem' }}>
          {FREE_ORDER.map(key => {
            const planet = allPlanets[key];
            if (!planet) return null;
            if (!canAccessPlanet(key, isPaid)) return null;
            const interp = interpretations[key];
            return (
              <UnlockedCard
                key={key}
                planetKey={key}
                planet={planet}
                text={loadingInterp ? '' : (interp?.text ?? '')}
              />
            );
          })}
        </div>

        {/* Soft paywall or locked planets */}
        {!isPaid && tier !== 'loading' && (
          <>
            <div style={{ marginBottom: '2rem' }}>
              <SoftPaywallBanner onClickUpgrade={openModal} source="mapa" />
            </div>

            <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'var(--text-22)', color: 'var(--bg)', margin: '0 0 1rem' }}>
              Planetas Bloqueados
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {LOCKED_ORDER.map(key => {
                const planet = allPlanets[key];
                if (!planet) return null;
                const interp = interpretations[key];
                return (
                  <LockedCard
                    key={key}
                    planetKey={key}
                    planet={planet}
                    preview={loadingInterp ? '' : (interp?.preview ?? '')}
                    onUnlock={openModal}
                  />
                );
              })}
            </div>
          </>
        )}

        {/* Paid: remaining planets unlocked */}
        {isPaid && (
          <>
            <h2 style={{ fontFamily: 'var(--font-display)', fontStyle: 'italic', fontWeight: 400, fontSize: 'var(--text-22)', color: 'var(--bg)', margin: '0 0 1rem' }}>
              Planetas Premium
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {getLockedPlanets().map(key => {
                const planet = allPlanets[key];
                if (!planet) return null;
                const interp = interpretations[key];
                return (
                  <UnlockedCard
                    key={key}
                    planetKey={key}
                    planet={planet}
                    text={loadingInterp ? '' : (interp?.text ?? interp?.preview ?? '')}
                  />
                );
              })}
            </div>
          </>
        )}
      </div>

      <UpsellModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}
